import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store/store';
import { fetchInvoices } from '../store/slices/invoiceSlice';
import { fetchABOFiles } from '../store/slices/aboSlice';
import { fetchUsers } from '../store/slices/userSlice';

export const useOptimizedDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounced refresh function to prevent excessive API calls
  const refreshData = useCallback(async () => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      // Fetch all data in parallel for better performance
      // For dashboard, we need ALL invoices to get accurate totals
      await Promise.all([
        dispatch(fetchInvoices({ limit: 1000 })).unwrap(), // Fetch up to 1000 invoices for dashboard stats
        dispatch(fetchABOFiles({})).unwrap(),
        dispatch(fetchUsers()).unwrap()
      ]);
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Dashboard data fetch error:', error);
    }
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    refreshData
  };
};
